/**
 * คำขอเปลี่ยนแปลงข้อมูลสินค้า — `GET`/`POST /v1/products/{id}/change-requests`
 *
 * นี่คือทางเดียวที่แก้ record ซึ่ง `ACTIVE` แล้วได้ (D-011) และเป็นทางเดียวที่
 * เปลี่ยนเป็น `INACTIVE` ได้ด้วย เพราะ D-042 ถอน `POST /products/{id}/deactivate`
 * ออกไปแล้ว — การปิด product เป็นการเปลี่ยนสถานะระดับ record จึงต้องผ่าน
 * approval ชุดเดียวกับการแก้อื่น ไม่ใช่ปุ่มลัดที่ไม่มีใครอนุมัติ
 *
 * สองก้อนที่หน้านี้ดึงมาและ **ต้องไม่ถูกเดาแทนกัน**:
 *
 * 1. `GET /v1/products/{id}` — ค่าจริงของ record ตอนนี้ ใช้บอกว่าข้อเสนอที่ค้างอยู่
 *    กำลังเสนอแก้อะไรจากค่าปัจจุบันอะไร และเป็นสิ่งที่ `409 stale_fields` อ้างถึง
 * 2. `GET /v1/products/{id}/change-requests` — ข้อเสนอทั้งหมดของ record นั้น
 *
 * ทั้งคู่ถูกดึงใหม่หลังคำตัดสินสำเร็จและหลัง `409` **หนึ่งครั้ง** ไม่มีการสั่งซ้ำ
 * อัตโนมัติ และไม่มีการเดาผลล่วงหน้า
 *
 * ไม่มีข้อมูลสินค้าหรือข้อเสนอถูกเขียนลง storage ใด ๆ (D-049 อนุญาตเฉพาะ session
 * token) — สำเนาของข้อเสนอที่ค้างอยู่ในเบราว์เซอร์ คือภาพเก่าของสิ่งที่คนอื่น
 * อาจเพิ่งอนุมัติไปแล้ว ซึ่งเป็นความผิดพลาดชนิดเดียวกับที่ทั้งกลไกนี้มีไว้ป้องกัน
 *
 * ⚠️ การเสนอ/ไม่เสนอฟอร์มและปุ่มที่นี่เป็น UX ไม่ใช่ด่านความปลอดภัย — flag อ่านจาก
 * `modules` ที่เซิร์ฟเวอร์ส่งมาผ่าน `can()` เท่านั้น ห้าม derive กติกา
 * ADMIN/AUDITOR ซ้ำจาก `system_roles` และเซิร์ฟเวอร์เป็นผู้ตัดสินทุกคำขอเสมอ
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ProductChangeRequestCard, type RefreshOutcome } from './ProductChangeRequestCard';
import { ProductChangeRequestForm } from './ProductChangeRequestForm';
import { ProductFailureNotice } from './ProductFailureNotice';
import {
  changeRequestStatusLabel,
  detailSearch,
  productStatusLabel,
  productStatusTone,
} from './productView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import {
  fetchProduct,
  fetchProductChangeRequests,
  type ProductFailure,
} from '../../lib/products/client';
import {
  PRODUCT_MASTER_MODULE,
  type Product,
  type ProductChangeRequest,
  type ProductChangeRequestListPage,
} from '../../lib/products/types';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';

export interface ProductChangeRequestViewProps {
  readonly productId: string;
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type RecordState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly product: Product }
  | ProductFailure;

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly page: ProductChangeRequestListPage }
  | ProductFailure;

/** สถานะเดียวของ record ที่ contract บอกว่าเสนอแก้ได้ ("ใช้เมื่อ record เป็น `ACTIVE` แล้ว") */
const PROPOSABLE_STATUS = 'ACTIVE';

export function ProductChangeRequestView({
  productId,
  permissions,
  apiBaseUrl,
  fetchImpl,
}: ProductChangeRequestViewProps) {
  const { pathname } = useLocation();
  const [record, setRecord] = useState<RecordState>({ status: 'loading' });
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [created, setCreated] = useState<ProductChangeRequest | null>(null);

  // ผลของ request ที่กลับมาหลัง component ถูกถอดออกแล้วต้องไม่ถูกเขียนลง state
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setRecord({ status: 'loading' });
    setList({ status: 'loading' });
    setCreated(null);

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchProduct(productId, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setRecord(result);
    });

    void fetchProductChangeRequests(productId, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setList(result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [productId, apiBaseUrl, fetchImpl]);

  /**
   * ดึงทั้งสองก้อนใหม่ **โดยไม่ล้างสิ่งที่แสดงอยู่ก่อน**
   *
   * ตั้งใจไม่กลับไปสถานะ "กำลังโหลด" เพราะการล้างหน้าจอจะพาคำอธิบายของ `409`
   * (ฟิลด์ไหนถูกแก้ไปแล้ว) หายไปพร้อมกัน ซึ่งเป็นข้อความที่ผู้ใช้ต้องอ่านพอดี
   * ถ้าดึงใหม่ไม่สำเร็จ ค่าที่แสดงอยู่ยังเป็นค่าเดิม และผู้เรียกได้ `failed`
   * กลับไปเพื่อบอกผู้ใช้ว่าสิ่งที่เห็นอาจไม่ใช่ของจริงแล้ว
   */
  const refresh = useCallback(async (): Promise<RefreshOutcome> => {
    const options = { apiBaseUrl, fetchImpl };
    const [nextRecord, nextList] = await Promise.all([
      fetchProduct(productId, options),
      fetchProductChangeRequests(productId, options),
    ]);
    if (!mounted.current) {
      return 'failed';
    }

    let outcome: RefreshOutcome = 'ok';
    if (nextRecord.status === 'ready') {
      setRecord(nextRecord);
    } else if (nextRecord.status !== 'cancelled') {
      outcome = 'failed';
    }
    if (nextList.status === 'ready') {
      setList(nextList);
    } else if (nextList.status !== 'cancelled') {
      outcome = 'failed';
    }
    return outcome;
  }, [productId, apiBaseUrl, fetchImpl]);

  const product = record.status === 'ready' ? record.product : null;
  const canEdit = can(permissions, PRODUCT_MASTER_MODULE, 'EDIT');
  const canApprove = can(permissions, PRODUCT_MASTER_MODULE, 'APPROVE');
  const proposable = product !== null && product.status === PROPOSABLE_STATUS;

  return (
    <div className="cap-page" data-testid="product-change-requests">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title" data-testid="product-cr-code">
            {product === null ? 'คำขอเปลี่ยนแปลงข้อมูลสินค้า' : product.product_code}
            <InfoHint
              label="เกี่ยวกับหน้าคำขอเปลี่ยนแปลงนี้"
              text="คำขอเปลี่ยนแปลงและการอนุมัติ (D-011/D-042)"
            />
          </h1>
          <p className="cap-page__sub">
            {product === null ? `รหัสภายใน ${productId}` : product.name}
          </p>
        </div>
        <div className="cap-page__actions">
          {product === null ? null : (
            <StatusBadge tone={productStatusTone(product.status)}>
              {productStatusLabel(product.status)}
            </StatusBadge>
          )}
          <Link
            className="cap-button cap-button--ghost"
            data-testid="product-cr-back"
            to={{ pathname, search: detailSearch(productId) }}
          >
            กลับไปหน้ารายละเอียด
          </Link>
        </div>
      </div>

      {record.status === 'loading' ? (
        <div className="cap-notice" data-testid="product-cr-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดข้อมูลสินค้า…</p>
        </div>
      ) : null}

      {record.status !== 'loading' && record.status !== 'ready' ? (
        <ProductFailureNotice failure={record} context="detail" />
      ) : null}

      {product === null || proposable ? null : (
        <div className="cap-notice" data-testid="product-cr-not-proposable" role="status">
          <StatusBadge tone="warning">ยังเสนอคำขอกับรายการนี้ไม่ได้</StatusBadge>
          <h2 className="cap-notice__title">
            รายการนี้อยู่ในสถานะ “{productStatusLabel(product.status)}”
          </h2>
          <p className="cap-notice__text">
            คำขอเปลี่ยนแปลงใช้กับรายการที่ <strong>ใช้งานแล้ว (ACTIVE)</strong> ตาม contract —
            รายการที่ยังเป็นร่างแก้ได้โดยตรงที่หน้าจอแก้ไข ส่วนรายการที่รออนุมัติหรือถูกปฏิเสธ
            ต้องเดินผ่าน workflow ของตัวเองก่อน (D-011) คำขอที่เคยยื่นไว้ยังแสดงอยู่ด้านล่าง
          </p>
        </div>
      )}

      {product === null || !proposable ? null : canEdit ? (
        <ProductChangeRequestForm
          product={product}
          apiBaseUrl={apiBaseUrl}
          fetchImpl={fetchImpl}
          onCreated={(changeRequest) => {
            setCreated(changeRequest);
            // แสดงรายการที่เซิร์ฟเวอร์เก็บไว้จริง ไม่ใช่ที่หน้าจอเดาว่าจะเป็น
            void refresh();
          }}
        />
      ) : (
        <div className="cap-notice" data-testid="product-cr-propose-unavailable" role="status">
          <StatusBadge tone="warning">ไม่ได้เปิดฟอร์มให้</StatusBadge>
          <h2 className="cap-notice__title">
            สิทธิ์ชุดนี้ไม่มีการเสนอคำขอเปลี่ยนแปลง
            <InfoHint
              label="เกี่ยวกับการไม่เปิดฟอร์มที่นี่"
              text="การไม่เปิดฟอร์มเป็นเพียง UX เพื่อไม่ให้กรอกงานที่จะถูกปฏิเสธตอนส่ง — ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ"
            />
          </h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_edit</code>{' '}
            ของโมดูล <code className="cap-table__code">PRODUCT_MASTER</code>{' '}
            หน้าจอนี้จึงไม่เปิดฟอร์มให้กรอก แต่ยังดูคำขอที่มีอยู่ได้
          </p>
        </div>
      )}

      {created === null ? null : (
        <div className="cap-banner" role="status" data-testid="product-cr-created">
          <strong>ส่งคำขอเปลี่ยนแปลงแล้ว</strong>
          <span>
            เซิร์ฟเวอร์บันทึกคำขอ <code className="cap-table__code">{created.change_request_id}</code>{' '}
            ไว้ในสถานะ “{changeRequestStatusLabel(created.status)}” — ค่าเดิม ณ ตอนเสนอ (baseline)
            ถูกเซิร์ฟเวอร์เก็บให้เอง และต้องมีผู้อื่นเป็นผู้อนุมัติ (D-010)
          </span>
        </div>
      )}

      <section className="cap-card" aria-label="คำขอเปลี่ยนแปลงของสินค้านี้">
        <div className="cap-card__head">
          <h2 className="cap-card__title">
            คำขอเปลี่ยนแปลงทั้งหมดของรายการนี้
            <InfoHint
              label="เกี่ยวกับค่าเดิม ณ ตอนเสนอคำขอ"
              text="ค่าเดิม ณ ตอนเสนอ (baseline) เป็นค่าที่เซิร์ฟเวอร์บันทึกไว้ตอนสร้างคำขอ — ถ้าไม่ตรงกับค่าจริงตอนนี้ แปลว่ามีคนแก้ไปก่อนแล้ว และการอนุมัติจะถูกปฏิเสธด้วย 409"
            />
          </h2>
        </div>

        {list.status === 'loading' ? (
          <p className="cap-card__foot" data-testid="product-cr-list-loading">
            กำลังโหลดคำขอเปลี่ยนแปลง…
          </p>
        ) : null}

        {list.status !== 'loading' && list.status !== 'ready' ? (
          <ProductFailureNotice failure={list} context="change-request" />
        ) : null}

        {list.status === 'ready' && list.page.items.length === 0 ? (
          <p className="cap-card__foot" data-testid="product-cr-empty">
            ยังไม่มีคำขอเปลี่ยนแปลงของรายการนี้
          </p>
        ) : null}
      </section>

      {list.status === 'ready' && list.page.items.length > 0 && product === null ? (
        <div className="cap-notice" data-testid="product-cr-needs-record" role="status">
          <p className="cap-notice__text">
            โหลดรายการคำขอได้ แต่โหลดข้อมูลสินค้าไม่สำเร็จ — หน้าจอจึงยังไม่แสดงคำขอ
            เพราะจะเทียบค่าที่เสนอกับค่าจริงตอนนี้ไม่ได้ และการอนุมัติโดยไม่เห็นค่าจริง
            คือสิ่งที่กลไกนี้มีไว้ป้องกัน
          </p>
        </div>
      ) : null}

      {list.status !== 'ready' || product === null
        ? null
        : list.page.items.map((changeRequest) => (
            <ProductChangeRequestCard
              key={changeRequest.change_request_id}
              changeRequest={changeRequest}
              product={product}
              canApprove={canApprove}
              apiBaseUrl={apiBaseUrl}
              fetchImpl={fetchImpl}
              onRefresh={refresh}
            />
          ))}
    </div>
  );
}
